import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit3, Power, Users, CreditCard, Network, Activity, Building2, FileText, Mail, Phone, MapPin, ExternalLink, X, RotateCcw, Loader2 } from 'lucide-react';
import { US_STATES, STATE_NAME } from '../../lib/usStates';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../../lib/api';
import { PROVIDER_TYPES } from '../../lib/pipStates';
import PageLoader from '../shared/PageLoader';
import './OrganizationProfile.css';

function initialsFor(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return '—';
  return format(new Date(iso), 'MMM d, yyyy');
}

function subStatusPill(sub, orgType) {
  if (orgType === 'law_firm' || orgType === 'marketer') return { text: 'Free', cls: 'muted' };
  if (!sub) return { text: 'No subscription', cls: 'warning' };
  if (sub.status === 'active')   return { text: 'Active', cls: 'success' };
  if (sub.status === 'trialing') return { text: 'In trial', cls: 'success' };
  if (sub.status === 'past_due') return { text: 'Past due', cls: 'warning' };
  if (sub.status === 'canceled') return { text: 'Canceled', cls: 'danger' };
  return { text: sub.status, cls: 'muted' };
}

const TABS = [
  { key: 'overview',     label: 'Overview',     Icon: Activity },
  { key: 'locations',    label: 'Locations',    Icon: MapPin },
  { key: 'members',      label: 'Members',      Icon: Users },
  { key: 'subscription', label: 'Subscription', Icon: CreditCard },
  { key: 'partnerships', label: 'Partnerships', Icon: Network },
  { key: 'settings',     label: 'Settings',     Icon: Edit3 },
];

export default function OrganizationProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [trialDays, setTrialDays] = useState(14);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialMsg, setTrialMsg] = useState('');
  const [refLinks, setRefLinks] = useState([]);
  const [refBusy, setRefBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.getOrgProfile(id)
      .then(d => { setData(d); setEdits({}); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.adminReferralLinks().then(d => setRefLinks(d.links || [])).catch(() => {}); }, []);

  // Who gets credit for this signup. Same control as Admin → Referral links;
  // a practice that came in without a link gets its owner here.
  const setReferralSource = async (code) => {
    setRefBusy(true); setSavedMsg('');
    try {
      await api.adminUpdateOrg(id, { referral_source: code || null });
      setSavedMsg(code ? `Credited to ${refLinks.find(l => l.code === code)?.label || code}` : 'Referral link cleared');
      refresh();
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setRefBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="op">
        <PageLoader variant="block" sub="Organization" label="Loading profile…" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="op">
        <div className="op-banner danger">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { org, members, subscriptions, partnerships, stats } = data;
  const locations = data.locations || [];
  const activeSub = subscriptions.find(s => ['active', 'trialing', 'past_due', 'incomplete'].includes(s.status));
  const subPill = subStatusPill(activeSub, org.type);
  const typeLabel = org.type === 'medical_practice' ? 'Medical Practice' : org.type === 'law_firm' ? 'Law Firm' : org.type === 'marketer' ? 'Marketer' : org.type;
  const typeClass = org.type === 'medical_practice' ? 'med' : 'law';

  const editing = (k, v) => setEdits(e => ({ ...e, [k]: v }));
  const cur = (k) => edits[k] !== undefined ? edits[k] : org[k];
  const dirty = Object.keys(edits).length > 0;

  const handleSave = async () => {
    setSaving(true); setSavedMsg('');
    try {
      await api.adminUpdateOrg(id, edits);
      setSavedMsg('Saved');
      refresh();
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Trial back on (or more of it): Stripe un-pauses / extends, or a fresh
  // trial subscription replaces the dead one. No card needed; the practice
  // is back in the app right away.
  const restartTrial = async () => {
    const verb = activeSub?.status === 'trialing' ? `Extend ${org.name}'s trial by ${trialDays} days` : `Give ${org.name} a new ${trialDays}-day free trial`;
    if (!window.confirm(`${verb}? They get full access immediately, no card required, and the trial pauses again if it ends without one.`)) return;
    setTrialBusy(true); setTrialMsg('');
    try {
      const out = await api.adminRestartTrial(id, trialDays);
      const until = out.trial_end ? format(new Date(out.trial_end), 'MMM d, yyyy') : '';
      setTrialMsg(`${out.how === 'extended' ? 'Trial extended' : out.how === 'resumed' ? 'Subscription resumed in trial' : 'New trial subscription created'} — runs until ${until}.`);
      refresh();
    } catch (err) {
      setTrialMsg(err.message);
    } finally {
      setTrialBusy(false);
    }
  };

  const toggleActive = async () => {
    const next = !org.is_active;
    if (!window.confirm(`${next ? 'Reactivate' : 'Deactivate'} ${org.name}?`)) return;
    try {
      await api.adminUpdateOrg(id, { is_active: next });
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="op">
      <button className="op-back" onClick={() => navigate('/admin/organizations')}>
        <ArrowLeft size={12} /> All organizations
      </button>

      {/* Hero */}
      <div className="op-hero">
        <div className={`op-avatar ${typeClass}`}>{initialsFor(org.name)}</div>
        <div className="op-meta">
          <div className="op-meta-row">
            <span className={`op-pill ${typeClass}`}>{typeLabel}</span>
            <span className={`op-pill ${org.is_active ? 'success' : 'danger'}`}>
              {org.is_active ? 'Active' : 'Deactivated'}
            </span>
            <span className={`op-pill ${subPill.cls}`}>{subPill.text}</span>
            {(org.telehealth_states || []).length > 0 && (
              <span className="op-pill med">Telehealth statewide · {org.telehealth_states.join(', ')}</span>
            )}
          </div>
          <h1 className="op-name">{org.name}</h1>
          <div className="op-sub">
            {(org.city || org.state) && (
              <span><MapPin size={11} /> {[org.city, org.state].filter(Boolean).join(', ')}</span>
            )}
            {org.phone && <span><Phone size={11} /> {org.phone}</span>}
            <span>Created {formatDistanceToNow(new Date(org.created_at), { addSuffix: true })}</span>
          </div>
        </div>
        <div className="op-hero-actions">
          <button className="op-btn op-btn-secondary" onClick={() => setTab('settings')}>
            <Edit3 size={12} /> Edit
          </button>
          <button className={`op-btn ${org.is_active ? 'op-btn-danger' : 'op-btn-secondary'}`} onClick={toggleActive}>
            <Power size={12} /> {org.is_active ? 'Deactivate' : 'Reactivate'}
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="op-stats">
        <div className="op-stat">
          <div className="op-stat-label">Team members</div>
          <div className="op-stat-value">{stats.members_count}</div>
        </div>
        <div className="op-stat">
          <div className="op-stat-label">Appointments / 30d</div>
          <div className="op-stat-value">{stats.appointments_30d}</div>
        </div>
        <div className="op-stat">
          <div className="op-stat-label">Active partnerships</div>
          <div className="op-stat-value">
            {stats.partnerships_active}
            {stats.partnerships_pending > 0 && <em> +{stats.partnerships_pending}</em>}
          </div>
          {stats.partnerships_pending > 0 && (
            <div className="op-stat-trend">{stats.partnerships_pending} pending</div>
          )}
        </div>
        <div className="op-stat">
          <div className="op-stat-label">Documents</div>
          <div className="op-stat-value">{stats.documents_count}</div>
          {stats.active_shares > 0 && (
            <div className="op-stat-trend">{stats.active_shares} shared with partners</div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="op-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`op-tab ${tab === t.key ? 'is-active' : ''}`} onClick={() => setTab(t.key)}>
            <t.Icon size={13} /> {t.label}
            {t.key === 'members' && <span className="op-tab-count">{stats.members_count}</span>}
            {t.key === 'locations' && locations.length > 0 && <span className="op-tab-count">{locations.length}</span>}
            {t.key === 'partnerships' && stats.partnerships_active > 0 && (
              <span className="op-tab-count">{stats.partnerships_active}</span>
            )}
          </button>
        ))}
      </div>

      {savedMsg && <div className="op-banner success">{savedMsg}</div>}

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="op-two-col">
          <div className="op-card">
            <h2 className="op-section-title">Profile</h2>
            <div>
              <div className="op-row"><span className="op-row-label">Type</span><span className="op-row-value">{typeLabel}</span></div>
              <div className="op-row"><span className="op-row-label">Address</span><span className="op-row-value">{[org.address_line1, org.city, org.state, org.zip].filter(Boolean).join(', ') || '—'}</span></div>
              <div className="op-row"><span className="op-row-label">Phone</span><span className="op-row-value">{org.phone || '—'}</span></div>
              <div className="op-row"><span className="op-row-label">Created</span><span className="op-row-value">{fmtDate(org.created_at)}</span></div>
              {org.type !== 'marketer' && (
                <div className="op-row">
                  <span className="op-row-label">Referral link</span>
                  <span className="op-row-value">
                    <select className="op-select" style={{ padding: '4px 8px', fontSize: 12 }} value={org.referral_source || ''} disabled={refBusy}
                      onChange={e => setReferralSource(e.target.value)}>
                      <option value="">No link — signed up directly</option>
                      {refLinks.map(l => <option key={l.id} value={l.code}>{l.label} (/r/{l.code}){l.active ? '' : ' — paused'}</option>)}
                      {org.referral_source && !refLinks.some(l => l.code === org.referral_source) && (
                        <option value={org.referral_source}>{org.referral_source} (unregistered code)</option>
                      )}
                    </select>
                  </span>
                </div>
              )}
              <div className="op-row"><span className="op-row-label">Org ID</span><span className="op-row-value" style={{ fontFamily: '"SF Mono", Menlo, monospace', fontSize: 11 }}>{org.id}</span></div>
            </div>
          </div>
          <div className="op-card">
            <h2 className="op-section-title">Quick facts</h2>
            <div>
              <div className="op-row">
                <span className="op-row-label">Subscription</span>
                <span className="op-row-value">
                  <span className={`op-pill ${subPill.cls}`}>{subPill.text}</span>
                </span>
              </div>
              {activeSub?.current_period_end && (
                <div className="op-row"><span className="op-row-label">Renews</span><span className="op-row-value">{fmtDate(activeSub.current_period_end)}</span></div>
              )}
              <div className="op-row"><span className="op-row-label">Stripe ID</span><span className="op-row-value" style={{ fontFamily: '"SF Mono", Menlo, monospace', fontSize: 11 }}>{org.stripe_customer_id || '—'}</span></div>
              <div className="op-row"><span className="op-row-label">Total subs</span><span className="op-row-value">{subscriptions.length}</span></div>
            </div>
          </div>
        </div>
      )}

      {tab === 'locations' && (
        <div className="op-card">
          <h2 className="op-section-title">Locations ({locations.length})</h2>
          {locations.length === 0 ? (
            <div className="op-empty"><MapPin size={28} /><div>No locations set up{org.type === 'medical_practice' ? ' — billing quantity may not match.' : '.'}</div></div>
          ) : (
            <div>
              {locations.map(l => (
                <div key={l.id} className="op-member-row">
                  <div className="op-member-avatar" style={{ background: 'rgba(13,167,202,0.12)', color: '#0DA7CA' }}>
                    <MapPin size={15} />
                  </div>
                  <div className="op-member-info">
                    <div className="op-member-name">
                      {l.name}
                      {l.is_primary && <span className="op-pill med" style={{ marginLeft: 8 }}>Primary</span>}
                    </div>
                    <div className="op-member-email">
                      {[l.address_line1, l.address_line2, l.city, l.state, l.zip].filter(Boolean).join(', ') || 'No address'}
                    </div>
                  </div>
                  <div className="op-member-meta">
                    {l.phone && <span><Phone size={10} style={{ display: 'inline', marginRight: 4 }} />{l.phone}</span>}
                    <span className={`op-pill ${l.lat != null ? 'success' : 'warning'}`}>
                      {l.lat != null ? 'On map' : 'Not geocoded'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'members' && (
        <div className="op-card">
          <h2 className="op-section-title">Team members ({members.length})</h2>
          {members.length === 0 ? (
            <div className="op-empty"><Users size={28} /><div>No members yet.</div></div>
          ) : (
            <div>
              {members.map(m => {
                const fullName = [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ') || m.user?.email;
                return (
                  <div key={m.id} className="op-member-row">
                    <div className="op-member-avatar">{initialsFor(fullName)}</div>
                    <div className="op-member-info">
                      <div className="op-member-name">
                        {fullName}
                        {m.user?.is_platform_admin && <span className="op-pill warning" style={{ marginLeft: 8 }}>Platform admin</span>}
                      </div>
                      <div className="op-member-email"><Mail size={10} style={{ display: 'inline', marginRight: 4 }} />{m.user?.email}</div>
                    </div>
                    <div className="op-member-meta">
                      <span className="op-pill muted">{m.role}</span>
                      {m.is_primary_org && <span className="op-pill med">Primary</span>}
                      <span>Joined {formatDistanceToNow(new Date(m.accepted_at || m.created_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'subscription' && org.type === 'medical_practice' && (
        <div className="op-card" style={{ marginBottom: 16 }}>
          <h2 className="op-section-title">Free trial</h2>
          <div className="op-row" style={{ alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div className="op-row-value">
                {!activeSub ? 'No live subscription'
                  : activeSub.status === 'trialing' ? `In trial${activeSub.current_period_end ? ` until ${fmtDate(activeSub.current_period_end)}` : ''}`
                  : activeSub.status === 'active' ? 'Active and paying'
                  : activeSub.status === 'past_due' ? 'Past due'
                  : activeSub.status}
                {!activeSub && subscriptions.some((s) => s.status === 'paused') && ' — trial ended without a card (paused)'}
              </div>
              <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: 'var(--muted)' }}>
                {activeSub?.status === 'active'
                  ? 'Nothing to restart: this practice is paying.'
                  : activeSub?.status === 'trialing'
                    ? 'Push the trial end out. The practice keeps full access with no card; it pauses again if the trial ends without one.'
                    : 'Start the free trial over. Stripe resumes the paused subscription (or a fresh trial replaces it); the practice is back in the app immediately, no card required.'}
              </div>
              {trialMsg && <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: /fail|error|refus|not /i.test(trialMsg) ? 'var(--danger)' : 'var(--success, #047857)' }}>{trialMsg}</div>}
            </div>
            {activeSub?.status !== 'active' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))} className="op-select" style={{ width: 'auto' }} disabled={trialBusy}>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                </select>
                <button type="button" className="op-btn op-btn-primary" onClick={restartTrial} disabled={trialBusy}>
                  {trialBusy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} {activeSub?.status === 'trialing' ? 'Extend trial' : 'Restart trial'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'subscription' && (
        <div className="op-card">
          <h2 className="op-section-title">Subscription history</h2>
          {(org.type === 'law_firm' || org.type === 'marketer') ? (
            <div className="op-empty"><CreditCard size={28} /><div>Law firms are free — no subscription required.</div></div>
          ) : subscriptions.length === 0 ? (
            <div className="op-empty"><CreditCard size={28} /><div>This organization has never had a subscription.</div></div>
          ) : (
            <>
              {subscriptions.map((s) => {
                const pill = subStatusPill(s, org.type);
                return (
                  <div key={s.id} className="op-row" style={{ alignItems: 'flex-start' }}>
                    <div>
                      <div className="op-row-value">{s.stripe_subscription_id}</div>
                      <div className="op-row-label" style={{ marginTop: 4 }}>
                        Created {fmtDate(s.created_at)}
                        {s.current_period_end && ` · Period end ${fmtDate(s.current_period_end)}`}
                        {s.canceled_at && ` · Canceled ${fmtDate(s.canceled_at)}`}
                      </div>
                    </div>
                    <span className={`op-pill ${pill.cls}`}>{pill.text}</span>
                  </div>
                );
              })}
              {org.stripe_customer_id && (
                <a
                  href={`https://dashboard.stripe.com/test/customers/${org.stripe_customer_id}`}
                  target="_blank" rel="noopener noreferrer"
                  className="op-btn op-btn-secondary"
                  style={{ marginTop: 16 }}
                >
                  Open in Stripe <ExternalLink size={12} />
                </a>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'partnerships' && (
        <div className="op-card">
          <h2 className="op-section-title">Partnerships ({partnerships.length})</h2>
          {partnerships.length === 0 ? (
            <div className="op-empty"><Network size={28} /><div>No partnerships yet.</div></div>
          ) : (
            <div>
              {partnerships.map(p => {
                const partner = p.requester_org_id === id ? p.target_org : p.requester_org;
                const direction = p.requester_org_id === id ? 'Outgoing' : 'Incoming';
                const cls = p.status === 'accepted' ? 'success' : p.status === 'pending' ? 'warning' : 'muted';
                return (
                  <div key={p.id} className="op-row">
                    <div>
                      <div className="op-row-value">{partner?.name || '—'}</div>
                      <div className="op-row-label" style={{ marginTop: 4 }}>
                        {direction} · {[partner?.city, partner?.state].filter(Boolean).join(', ') || '—'} · {fmtDate(p.requested_at)}
                      </div>
                    </div>
                    <span className={`op-pill ${cls}`}>{p.status}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <div className="op-card">
          <h2 className="op-section-title">Edit organization</h2>
          <div className="op-form">
            <div>
              <label className="op-field-label">Name</label>
              <input className="op-input" value={cur('name') || ''} onChange={e => editing('name', e.target.value)} />
            </div>
            <div className="op-form-row">
              <div>
                <label className="op-field-label">Type</label>
                <select className="op-select" value={cur('type') || ''} onChange={e => editing('type', e.target.value)}>
                  <option value="medical_practice">Medical Practice</option>
                  <option value="law_firm">Law Firm</option>
                </select>
              </div>
              <div>
                <label className="op-field-label">Phone</label>
                <input className="op-input" value={cur('phone') || ''} onChange={e => editing('phone', e.target.value)} />
              </div>
            </div>
            {/* Provider type drives per-location pricing. "Not set" keeps the
                org on the legacy state-only rule (pre-032 grandfathering). */}
            {cur('type') === 'medical_practice' && (
              <div>
                <label className="op-field-label">Provider type</label>
                <select
                  className="op-select"
                  value={cur('provider_type') || ''}
                  onChange={e => editing('provider_type', e.target.value || null)}
                >
                  <option value="">Not set — legacy state-based pricing</option>
                  {PROVIDER_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Statewide telehealth (071): platform-set, the practice never
                configures this. Listed for these states in the directory and
                always a concierge candidate for patients there. */}
            {cur('type') === 'medical_practice' && (() => {
              const picked = Array.isArray(cur('telehealth_states')) ? cur('telehealth_states') : [];
              return (
                <div>
                  <label className="op-field-label">Telehealth — serves these whole states by video</label>
                  <div className="op-chips">
                    {picked.map(st => (
                      <span key={st} className="op-chip">
                        {STATE_NAME[st] || st}
                        <button type="button" aria-label={`Remove ${st}`} onClick={() => editing('telehealth_states', picked.filter(x => x !== st))}><X size={11} strokeWidth={2.5} /></button>
                      </span>
                    ))}
                    <select
                      className="op-select"
                      value=""
                      onChange={e => { if (e.target.value) editing('telehealth_states', [...picked, e.target.value]); }}
                    >
                      <option value="">{picked.length ? 'Add another state…' : 'Add a state… (leave empty for an in-person practice)'}</option>
                      {US_STATES.filter(([code]) => !picked.includes(code)).map(([code, name]) => (
                        <option key={code} value={code}>{name} ({code})</option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })()}
            <div>
              <label className="op-field-label">Address</label>
              <input className="op-input" value={cur('address_line1') || ''} onChange={e => editing('address_line1', e.target.value)} />
            </div>
            <div className="op-form-row-3">
              <div>
                <label className="op-field-label">City</label>
                <input className="op-input" value={cur('city') || ''} onChange={e => editing('city', e.target.value)} />
              </div>
              <div>
                <label className="op-field-label">State</label>
                <input className="op-input" value={cur('state') || ''} maxLength={2} onChange={e => editing('state', e.target.value.toUpperCase())} />
              </div>
              <div>
                <label className="op-field-label">ZIP</label>
                <input className="op-input" value={cur('zip') || ''} onChange={e => editing('zip', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={handleSave} disabled={!dirty || saving} className="op-btn op-btn-primary">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {dirty && (
                <button onClick={() => setEdits({})} className="op-btn op-btn-secondary">Discard</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
